import { signal } from "@preact/signals";
import { loadSettings, mergeSettings, saveSettings } from "./persist";
import type { SettingsPatch, SettingsStorage } from "./persist";
import { SETTINGS_KEY, defaultKeybinds, defaultSettings } from "./schema";
import type { Settings, SettingsSection } from "./schema";

function browserStorage(): SettingsStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null; // access denied (sandboxed iframe, blocked storage)
  }
}

const storage = browserStorage();

/** Live settings. Read `settings.value` anywhere; subscribe with `effect` or by rendering it. */
export const settings = signal<Settings>(loadSettings(storage));

/**
 * Merge `patch` into the settings (validated and clamped), apply it instantly and persist it.
 * Keybinds are patched per action (the array replaces that action's bindings).
 */
export function updateSettings(patch: SettingsPatch): void {
  const next = mergeSettings(settings.value, patch);
  settings.value = next;
  saveSettings(storage, next);
}

/** Replace the whole settings object (still sanitized). */
export function replaceSettings(next: Settings): void {
  const merged = mergeSettings(defaultSettings(), next);
  settings.value = merged;
  saveSettings(storage, merged);
}

/** Restore the defaults of one section (not keybinds: see `resetKeybinds`). */
export function resetSection(section: SettingsSection): void {
  const defaults = defaultSettings();
  replaceSettings(Object.assign({}, settings.value, { [section]: defaults[section] }));
}

export function resetKeybinds(): void {
  updateSettings({ keybinds: defaultKeybinds() });
}

// Keep several tabs in sync.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === SETTINGS_KEY) settings.value = loadSettings(storage);
  });
}
